import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';

interface LayoutProps {
    children: ReactNode;
}

const DrawerContext = createContext<{ open: () => void }>({ open: () => undefined });

/**
 * Lets a page's own header render the mobile menu button inline, instead of a
 * floating button that would have to out-z-index every modal on the page.
 */
export const useDrawer = () => useContext(DrawerContext);

const Layout: React.FC<LayoutProps> = ({ children }) => {
    // Drawer state is mobile-only; at md+ the sidebar is always visible and this
    // is never read.
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const location = useLocation();

    // Close the drawer on navigation so it never covers the page it opened.
    useEffect(() => {
        setIsDrawerOpen(false);
    }, [location.pathname]);

    // Stop the page scrolling behind the open drawer.
    useEffect(() => {
        if (!isDrawerOpen) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, [isDrawerOpen]);

    return (
        <DrawerContext.Provider value={{ open: () => setIsDrawerOpen(true) }}>
            {/* app-shell carries the mobile-only 100dvh (see index.css); an inline
                style would win at every width and alter the desktop height. */}
            <div className="app-shell flex min-h-screen bg-background">
                <Sidebar isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />

                {/* Backdrop — mobile only, sits under the drawer (z-40) and over content. */}
                {isDrawerOpen && (
                    <div
                        className="fixed inset-0 z-[55] bg-black/50 md:hidden"
                        onClick={() => setIsDrawerOpen(false)}
                        aria-hidden="true"
                    />
                )}

                <main className="flex-1 min-w-0 md:ml-72">
                    {children}
                </main>
            </div>
        </DrawerContext.Provider>
    );
};

export default Layout;
